import React, { useMemo } from 'react'
import { Avatar, Layout, Menu } from 'antd'
import {
  AlertOutlined,
  AuditOutlined,
  BarChartOutlined,
  DashboardOutlined,
  FileTextOutlined,
  FlagOutlined,
  LineChartOutlined,
  RocketOutlined,
  SettingOutlined,
  TeamOutlined,
} from '@ant-design/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppDispatch, useAppSelector } from '../../hooks/hooks'
import { setSidebarCollapsed } from '../../store/slices/UISlice'
import { useTranslation } from '../../hooks/useTranslation'
import { LAYOUT } from '../../constants/constants'
import SubAppTile from './Sidebar/SubAppTile'
import SidebarRailItem from './Sidebar/SidebarRailItem'

const { Sider } = Layout

interface NavEntry {
  key: string
  label: string
  icon: React.ReactNode
}

const Sidebar: React.FC = () => {
  const collapsed = useAppSelector((state) => state.ui.sidebarCollapsed)
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const { t } = useTranslation()

  const navItems: NavEntry[] = useMemo(
    () => [
      { key: '/dashboard',  label: t('navigation.dashboard'),                icon: <DashboardOutlined /> },
      { key: '/issues',     label: t('navigation.modules.incidents.title'), icon: <AlertOutlined /> },
      { key: '/events',     label: t('navigation.modules.events.title'),    icon: <AuditOutlined /> },
      { key: '/risks',      label: t('navigation.modules.risks.title'),     icon: <FlagOutlined /> },
      { key: '/plans',      label: t('navigation.modules.plans.title'),     icon: <RocketOutlined /> },
      { key: '/indicators', label: t('navigation.modules.indicators.title'),icon: <LineChartOutlined /> },
      { key: '/reports',    label: t('navigation.reports'),                 icon: <BarChartOutlined /> },
      { key: '/users',      label: 'Users',                                 icon: <TeamOutlined /> },
      { key: '/documents',  label: t('navigation.documents'),               icon: <FileTextOutlined /> },
      { key: '/settings',   label: t('navigation.settings'),                icon: <SettingOutlined /> },
    ],
    [t],
  )

  const activeKey = navItems
    .map((i) => i.key)
    .find((k) => location.pathname === k || location.pathname.startsWith(k + '/'))

  const goHome = () => navigate('/')

  return (
    <Sider
      className="gt-sidebar"
      width={LAYOUT.SIDEBAR_WIDTH}
      collapsedWidth={LAYOUT.SIDEBAR_COLLAPSED_WIDTH}
      collapsible
      collapsed={collapsed}
      onCollapse={(v) => dispatch(setSidebarCollapsed(v))}
      trigger={null}
    >
      {collapsed ? (
        <div className="gt-sidebar-rail">
          <SubAppTile onClick={goHome} />
          <div className="gt-sidebar-rail-divider" role="separator" />
          <nav className="gt-sidebar-rail-nav" aria-label="Primary">
            {navItems.map((item) => (
              <SidebarRailItem
                key={item.key}
                icon={item.icon}
                label={item.label}
                active={activeKey === item.key}
                onClick={() => navigate(item.key)}
              />
            ))}
          </nav>
          <div className="gt-sidebar-rail-spacer" />
          <Avatar size={32} className="gt-sidebar-rail-avatar">
            JD
          </Avatar>
        </div>
      ) : (
        <div className="gt-sidebar-expanded">
          <div className="gt-sidebar-expanded-brand" onClick={goHome} role="button" tabIndex={0}>
            <SubAppTile />
            <span className="gt-sidebar-expanded-brand-label">Giro UI</span>
          </div>
          <div className="gt-sidebar-rail-divider" role="separator" />
          <Menu
            mode="inline"
            className="gt-sidebar-menu"
            selectedKeys={activeKey ? [activeKey] : []}
            onClick={({ key }) => navigate(key)}
            items={navItems.map((i) => ({ key: i.key, icon: i.icon, label: i.label }))}
          />
        </div>
      )}
    </Sider>
  )
}

export default Sidebar
